import { PaginationState } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../../components';
import { ACTIVE_TAB_PARAM, AppRoutes } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { CallProcessingStatus, CustomSortingState, ReviewFilters, ReviewTab as ReviewTabType } from '../../../types';
import { isCallInProgress, isCallProcessingPending } from '../../../utils';
import useCallActions from './useCallActions';
import useReviewTabColumns from './useReviewTabColumns';
import ReviewPageEmptyState from './ReviewPageEmptyState';

interface ReviewPageContentProps {
  isLoading: boolean;
  filters: ReviewFilters;
  totalPages: number;
  pagination: PaginationState;
  sorting: CustomSortingState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  setSorting: Dispatch<SetStateAction<CustomSortingState>>;
  setIsImportRealCallsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const ReviewPageContent = ({
  sorting,
  setSorting,
  isLoading,
  filters,
  pagination,
  setPagination,
  totalPages,
  setIsImportRealCallsModalOpen,
}: ReviewPageContentProps) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get(ACTIVE_TAB_PARAM) as ReviewTabType;

  const { calls, currentCallDetails } = useAppSelector((state) => state.review);

  // State to track the active row.
  const [activeRowIndex, setActiveRowIndex] = useState<number | undefined>(undefined);

  // Get the columns for the review tab.
  const { simulationColumns, realCallsColumns } = useReviewTabColumns({ sorting, setSorting });

  const isEmpty = !isLoading && !calls.length;

  // Set the active row index to the call that is currently being played.
  useEffect(() => {
    if (currentCallDetails) {
      const index = calls.findIndex((call) => call.callSid === currentCallDetails.call.callSid);
      if (index === -1) {
        // If the call is not in the list, reset the active row index.
        // Happens when the user filters the calls and the call currently being played is no longer in the list.
        setActiveRowIndex(undefined);
      } else if (index !== activeRowIndex) {
        // If the call is in the list and the active row index changed,
        // update the active row index.
        setActiveRowIndex(index);
      }
    } else if (activeRowIndex !== undefined) {
      // Reset the active row index when the media player closes.
      setActiveRowIndex(undefined);
    }

    // We purposely don't include activeRowIndex in the dependency array
    // because we only want to run this effect when the callSid or filters change.
  }, [currentCallDetails, calls, filters, setActiveRowIndex]);

  const parsedData = useMemo(
    () =>
      calls.map((call) => {
        const { callSid, processingStatus, scoringStatus } = call;
        const isProcessed = processingStatus === CallProcessingStatus.PROCESSED;
        const isProcessing = processingStatus === CallProcessingStatus.PROCESSING;
        const isScored = scoringStatus === CallProcessingStatus.PROCESSED;

        // Allow call review if call has finished processing or scoring.
        const canReviewCall = isProcessed || isScored;

        const getCallTooltip = () => {
          if (canReviewCall) return 'Review call';
          if (isProcessing) return 'Processing...';
          if (isCallProcessingPending(call)) return "Processing hasn't started";
          if (isCallInProgress(call)) return 'Call in progress';
          return 'Processing error';
        };

        const onCallClick = () => {
          // Clicking on the call will navigate to the call details page.
          navigate({
            pathname: `${AppRoutes.REVIEW}/${callSid}`,
            search: window.location.search,
          });
        };

        return {
          ...call,
          tooltip: getCallTooltip(),
          onClick: canReviewCall ? onCallClick : undefined,
        };
      }),
    [calls, navigate]
  );

  if (isEmpty) {
    return (
      <ReviewPageEmptyState
        filters={filters}
        activeTab={activeTab}
        setIsImportRealCallsModalOpen={setIsImportRealCallsModalOpen}
      />
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-4 pl-8 pr-4">
      <DataTable
        activeRowIndices={activeRowIndex === undefined ? undefined : [activeRowIndex]}
        columns={activeTab === ReviewTabType.SIMULATIONS ? simulationColumns : realCallsColumns}
        data={parsedData}
        isLoading={isLoading}
        paginationControls={{ pagination, totalPages, setPagination }}
        useEndActions={(call, closeActions) => useCallActions(call, closeActions)}
      />
    </div>
  );
};

export default ReviewPageContent;
